import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  useTheme,
  Box,
  IconButton,
  Slide,
  Divider,
  useMediaQuery
} from '@mui/material';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Delete",
  message = "Are you sure you want to delete this item?",
  confirmText = "Delete",
  cancelText = "Cancel",
  type = "delete"
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Color scheme based on dialog type
  const colorMap = {
    delete: {
      main: theme.palette.error.main,
      light: theme.palette.error.light,
      contrast: theme.palette.error.contrastText
    },
    warning: {
      main: theme.palette.warning.main,
      light: theme.palette.warning.light,
      contrast: theme.palette.warning.contrastText
    },
    info: {
      main: theme.palette.info.main,
      light: theme.palette.info.light,
      contrast: theme.palette.info.contrastText
    }
  };

  const colors = colorMap[type] || colorMap.delete;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth={isMobile ? "auto" : 400}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: `0 10px 25px -5px rgba(0,0,0,0.2)`,
          border: `1px solid ${colors.main}`,
          mx: isMobile ? 2 : 'auto'
        }
      }}
    >
      <Box
        sx={{
          bgcolor: colors.light,
          color: colors.main,
          p: 2,
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <WarningIcon fontSize="large" sx={{ mr: 2 }} />
        <DialogTitle sx={{ 
          p: 0, 
          color: 'white',
          fontWeight: 600,
          flexGrow: 1
        }}>
          {title}
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ py: 3, px: 4 }}>
        <DialogContentText sx={{ 
          fontSize: '1.1rem',
          color: 'text.primary'
        }}>
          {message}
        </DialogContentText>
      </DialogContent>

      <Divider sx={{ my: 1 }} />

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          flexDirection: isMobile ? 'row' : 'row',
          gap: isMobile ? 2 : 0,
          justifyContent: isMobile ? 'center' : 'space-between',
          alignItems: isMobile ? 'center' : 'center'
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          fullWidth={isMobile}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              bgcolor: 'action.hover'
            }
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          fullWidth={isMobile}
          sx={{
            bgcolor: colors.main,
            color: colors.contrast,
            borderRadius: 2,
            px: 3,
            py: 1,
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: colors.main,
              boxShadow: `0 3px 10px ${colors.main}80`,
              transform: 'translateY(-1px)'
            },
            transition: 'all 0.2s ease'
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
